import { Link } from 'react-router-dom';
import { ShieldCheck, Info, ArrowLeft } from 'lucide-react';
import { Button } from '../../components/ui/button';

export default function PrivacyPage() {
  return (
    <div className="min-h-screen bg-white">
      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center">
              <img src="/logo.png" alt="Reliefify" className="h-10 w-auto object-contain" />
            </Link>
            <Link to="/">
              <Button variant="ghost" className="h-10 rounded-full text-sm font-medium">
                <ArrowLeft className="w-4 h-4 mr-1.5" /> Back to Home
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      <main className="pt-28 pb-20 md:pt-36 md:pb-28">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-emerald-100 mb-5">
              <ShieldCheck className="w-8 h-8 text-emerald-700" />
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Privacy &amp; Student Data
            </h1>
            <p className="text-lg text-slate-500">How Reliefify protects student privacy</p>
          </div>

          {/* Main content */}
          <div className="prose-slate space-y-8" data-testid="privacy-page-content">
            <section>
              <h2 className="text-xl font-semibold text-slate-900 mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Student Privacy First
              </h2>
              <div className="space-y-4 text-slate-600 leading-relaxed">
                <p>
                  Reliefify was designed specifically for casual teachers, with student privacy in mind.
                </p>
                <p>
                  The app is not a student management system and does not connect to school databases. Instead, it simply helps teachers organise their day and manage classes while they are teaching.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-slate-900 mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Data Minimisation
              </h2>
              <div className="space-y-4 text-slate-600 leading-relaxed">
                <p>
                  Reliefify follows a <strong className="text-slate-800">data-minimisation approach</strong>, meaning the app only uses the minimum information needed for teachers to run a class.
                </p>
                <ul className="space-y-3 my-4 pl-1">
                  {[
                    'Teachers typically enter shortened student names or initials (for example: Smith J.)',
                    'Reliefify does not require full student records',
                    'The app does not access school databases',
                    'Student information is not shared with third parties',
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <ShieldCheck className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <p>
                  Any names entered are simply used by the teacher for tools like seating plans, class lists, or lesson summaries during the school day.
                </p>
                <p>
                  Reliefify was built to support teachers without collecting unnecessary student data.
                </p>
              </div>
            </section>

            {/* Privacy Tip box */}
            <div className="bg-slate-50 border border-emerald-200 rounded-2xl p-6 sm:p-8" data-testid="privacy-tip-box">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0">
                  <Info className="w-5 h-5 text-emerald-700" />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Privacy Tip for Teachers
                  </h3>
                  <p className="text-sm sm:text-base text-slate-600 leading-relaxed">
                    To protect student privacy, we recommend entering initials or shortened names only when creating class lists.
                  </p>
                  <div className="mt-3 bg-white rounded-lg px-4 py-3 inline-block border border-slate-200">
                    <p className="text-sm text-slate-500">Example:</p>
                    <p className="text-sm text-slate-800 font-medium">
                      John Smith &rarr; <span className="text-emerald-700">Smith J.</span>
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <section>
              <h2 className="text-xl font-semibold text-slate-900 mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
                What Data Does Reliefify Collect?
              </h2>
              <div className="space-y-4 text-slate-600 leading-relaxed">
                <p>To provide the service, Reliefify stores:</p>
                <ul className="space-y-2 pl-1">
                  {[
                    'Your name and email address (for your account)',
                    'School names and class names you create',
                    'Student names or initials you enter',
                    'Attendance records, behaviour logs, and lesson notes you record',
                    'AI-generated summary reports',
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full mt-2.5 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <p>
                  This data is stored securely and is only accessible to you through your account. It is not shared with schools, other teachers, or third parties.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-slate-900 mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Your Rights
              </h2>
              <div className="space-y-4 text-slate-600 leading-relaxed">
                <p>You can:</p>
                <ul className="space-y-2 pl-1">
                  {[
                    'Delete individual classes, students, or records at any time',
                    'Request a full account deletion by contacting us',
                    'Export your data if needed',
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full mt-2.5 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-slate-900 mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Contact
              </h2>
              <p className="text-slate-600 leading-relaxed">
                If you have any questions about privacy or how your data is handled, please contact us at{' '}
                <a href="mailto:support@reliefify.com.au" className="text-emerald-700 hover:text-emerald-800 font-medium">
                  support@reliefify.com.au
                </a>
              </p>
            </section>
          </div>

          <div className="mt-12 text-center">
            <Link to="/">
              <Button variant="outline" className="h-12 px-8 rounded-full text-base font-medium">
                <ArrowLeft className="w-4 h-4 mr-2" /> Back to Home
              </Button>
            </Link>
          </div>
        </div>
      </main>

      {/* Simple footer */}
      <footer className="bg-slate-900 text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-slate-400 text-sm">
            &copy; {new Date().getFullYear()} Reliefify. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
